import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, Grid, IconButton,
  InputAdornment, LinearProgress, MenuItem, Pagination, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AddIcon from "@mui/icons-material/Add";
import { Link } from "react-router-dom";
import { getPowerCustomers, updatePowerCustomerTags } from "../../services/powerCustomer";
import { user as userRoutes } from "../../routes/route";
import { toastMessage } from "../../utils/toastMessage";

const todayInIndia = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit"
}).format(new Date());

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const SummaryCard = ({ icon, value, label, note, color }) => (
  <Card variant="outlined" sx={{ height: "100%", borderRadius: 3 }}>
    <CardContent sx={{ display: "flex", gap: 2, alignItems: "center" }}>
      <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: `${color}18`, color, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={650}>{value}</Typography>
        <Typography variant="body2" fontWeight={500}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{note}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const PowerCustomer = () => {
  const [startDate, setStartDate] = useState(todayInIndia());
  const [endDate, setEndDate] = useState(todayInIndia());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [displayLimit, setDisplayLimit] = useState(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [tagDrafts, setTagDrafts] = useState({});
  const [savingTagUserId, setSavingTagUserId] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    if (!startDate || !endDate || startDate > endDate) {
      setError("Start date must be before or equal to end date.");
      setLoading(false);
      return () => { active = false; };
    }
    getPowerCustomers({ startDate, endDate })
      .then((result) => active && setData(result))
      .catch((err) => active && setError(err.message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [startDate, endDate]);

  const visibleCandidates = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!data?.candidates) return [];
    return data.candidates.filter((item) => !query ||
      [item.name, item.phone, item.email, item.concern, item.consultantName, ...(item.tags || [])]
        .some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [data, search]);

  const rowsPerPage = displayLimit;
  const totalPages = Math.max(1, Math.ceil(visibleCandidates.length / rowsPerPage));
  const paginatedCandidates = useMemo(() => {
    const offset = (currentPage - 1) * rowsPerPage;
    return visibleCandidates.slice(offset, offset + rowsPerPage);
  }, [visibleCandidates, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, displayLimit, startDate, endDate]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const saveTags = async (item, tags) => {
    try {
      setSavingTagUserId(item.userId);
      const record = await updatePowerCustomerTags(item.userId, tags);
      setData((current) => current ? {
        ...current,
        candidates: (current.candidates || []).map((candidate) =>
          candidate.userId === item.userId ? { ...candidate, tags: record.tags || [] } : candidate
        ),
        loyalCustomers: (current.loyalCustomers || []).map((customer) =>
          customer.userId === item.userId ? { ...customer, tags: record.tags || [] } : customer
        ),
      } : current);
      toastMessage("Power Customer tags updated", "success");
    } catch (err) {
      toastMessage(err.message || "Unable to update tags", "error");
    } finally {
      setSavingTagUserId("");
    }
  };

  const addTag = (item) => {
    const value = String(tagDrafts[item.userId] || "").trim();
    if (!value) return;
    setTagDrafts((drafts) => ({ ...drafts, [item.userId]: "" }));
    saveTags(item, [...new Set([...(item.tags || []), value])]);
  };

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight={650} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ElectricBoltIcon color="warning" /> Power Customers
              </Typography>
              <Typography color="text.secondary">
                Top 10% first-consult users most likely to spend {money(data?.targetFutureSpend || 1500)} in future.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <TextField
                label="Start date"
                type="date"
                size="small"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End date"
                type="date"
                size="small"
                value={endDate}
                inputProps={{ min: startDate }}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {loading && (
            <Box
              role="status"
              aria-label="Loading Power Customer data"
              sx={{
                position: "fixed",
                top: { xs: 0, md: 75 },
                right: 0,
                bottom: 0,
                left: { xs: 0, md: 265 },
                zIndex: 1400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <CircularProgress size={54} thickness={4} />
            </Box>
          )}

          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard icon={<PeopleAltOutlinedIcon />} value={data?.totalFirstConsultations || 0}
                label="First consultations" note="First-ever consult completed" color="#2563eb" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard icon={<ElectricBoltIcon />} value={data?.selectedCount || 0}
                label="Power customers" note="Highest scoring top 10%" color="#f59e0b" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard icon={<CurrencyRupeeIcon />} value={money((data?.selectedCount || 0) * (data?.targetFutureSpend || 1500))}
                label="Target potential" note="Candidates × ₹1,500 target" color="#16a34a" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <SummaryCard icon={<InfoOutlinedIcon />} value={`${data?.selectionRate || 10}%`}
                label="Selection quota" note="Rounded up to next user" color="#7c3aed" />
            </Grid>
          </Grid>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                How the score is decided
                <Tooltip title="This is a transparent rule-based propensity score, not a guaranteed revenue prediction. Validate and tune weights using actual 90-day repeat-spend data.">
                  <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Every eligible user gets a score out of 100. Users are ranked by score and the top 10% receive the Power Customer label.
              </Typography>
              <Grid container spacing={1.5}>
                {(data?.formula || []).map((item) => (
                  <Grid item xs={12} sm={6} lg key={item.key}>
                    <Tooltip title={item.description} arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, height: "100%", cursor: "help", borderRadius: 2 }}>
                        <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
                        <Typography variant="h6" color="primary">{item.maxPoints} pts</Typography>
                        {/* <Typography variant="caption" color="text.secondary">Hover for formula</Typography> */}
                      </Paper>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 2, display: "flex", gap: 2, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              <Typography variant="h6" fontWeight={600}>Ranked customers</Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  select
                  size="small"
                  label="Users to show"
                  value={displayLimit}
                  onChange={(event) => setDisplayLimit(Number(event.target.value))}
                  sx={{ minWidth: 135 }}
                >
                  {[30, 50, 100, 250, 500, 700, 1000, 1500, 2000].map((value) => (
                    <MenuItem key={value} value={value}>{value}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small" placeholder="Search name, phone, concern..."
                  value={search} onChange={(event) => setSearch(event.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                />
              </Box>
            </Box>
            <TableContainer>
              <Table sx={{ minWidth: 1050 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc", "& .MuiTableCell-head": { fontWeight: 500 } }}>
                    <TableCell>Rank / Customer</TableCell>
                    <TableCell>Consultation</TableCell>
                    <TableCell>Score</TableCell>
                    {(data?.formula || []).map((item) => (
                      <TableCell key={item.key} align="center">
                        <Tooltip title={item.description} arrow>
                          <Box component="span" sx={{ cursor: "help", borderBottom: "1px dotted" }}>{item.label}</Box>
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell>Support Tags</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && visibleCandidates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={(data?.formula?.length || 0) + 5} align="center" sx={{ py: 6 }}>
                        No matching first consultations found.
                      </TableCell>
                    </TableRow>
                  )}
                  {paginatedCandidates.map((item) => (
                    <TableRow key={item.userId} hover sx={item.isPowerCustomer ? { bgcolor: "#fffbeb" } : undefined}>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                          <Typography fontWeight={600} color="text.secondary">#{item.rank}</Typography>
                          <Box>
                            <Typography fontWeight={550}>{item.name}</Typography>
                            <Typography variant="caption" color="text.secondary" component="div">
                              ID:{" "}
                              <Link
                                to={`${userRoutes.USER_DETAIL}/${item.userId}`}
                                style={{ color: "#1976d2", textDecoration: "underline" }}
                              >
                                {item.customerId || item.userId}
                              </Link>
                              {item.phone ? ` · ${item.phone}` : ""}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{item.concern}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.consultantName} · {item.metrics.durationMinutes} min</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 130 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Typography fontWeight={600}>{item.score}</Typography>
                          <LinearProgress variant="determinate" value={item.score} sx={{ width: 65, height: 7, borderRadius: 4 }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary">{item.confidence} signal</Typography>
                      </TableCell>
                      {(data?.formula || []).map((formula) => (
                        <TableCell key={formula.key} align="center">
                          <Tooltip title={`${formula.description} Current input: ${
                            formula.key === "extendedFirstConsult" ? `${item.metrics.durationMinutes} min actual vs 10 min standard — ${item.metrics.didExtend ? "Extended" : "Not extended"}` :
                            formula.key === "firstConsultDuration" ? `${item.metrics.durationMinutes} minutes completed` :
                            formula.key === "sameDayRecharges" ? `${item.metrics.dailyRechargeCount} successful recharge(s) today` :
                            formula.key === "additionalConsultations" ? `${item.metrics.additionalConsultations} additional; ${item.metrics.totalConsultationsToday} total today` :
                            `${item.metrics.consultantPattern}; ${item.metrics.uniqueConsultants} unique consultant(s)`
                          }`} arrow>
                            <Chip size="small" label={`${item.breakdown[formula.key]}/${formula.maxPoints}`} sx={{ cursor: "help" }} />
                          </Tooltip>
                        </TableCell>
                      ))}
                      <TableCell sx={{ minWidth: 160 }}>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(item.tags || []).length
                            ? item.tags.map((tag) => (
                                <Chip
                                  key={tag}
                                  size="small"
                                  label={tag}
                                  variant="outlined"
                                  onDelete={item.isPowerCustomer
                                    ? () => saveTags(item, item.tags.filter((value) => value !== tag))
                                    : undefined}
                                  disabled={savingTagUserId === item.userId}
                                />
                              ))
                            : <Typography variant="caption" color="text.secondary">No tags</Typography>}
                        </Box>
                        {item.isPowerCustomer && (
                          <Box sx={{ display: "flex", gap: 0.5, mt: 1, alignItems: "center" }}>
                            <TextField
                              size="small"
                              placeholder="Add tag"
                              value={tagDrafts[item.userId] || ""}
                              inputProps={{ maxLength: 40 }}
                              onChange={(event) => setTagDrafts((drafts) => ({
                                ...drafts,
                                [item.userId]: event.target.value,
                              }))}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  addTag(item);
                                }
                              }}
                              sx={{ minWidth: 115 }}
                            />
                            <Tooltip title="Add support tag">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => addTag(item)}
                                  disabled={
                                    savingTagUserId === item.userId ||
                                    !String(tagDrafts[item.userId] || "").trim()
                                  }
                                >
                                  {savingTagUserId === item.userId
                                    ? <CircularProgress size={18} />
                                    : <AddIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.isPowerCustomer
                          ? <Chip icon={<ElectricBoltIcon />} label="Power Customer" color="warning" size="small" />
                          : <Chip label="Not selected" variant="outlined" size="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {visibleCandidates.length > 0 && (
              <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderTop: "1px solid #e5e7eb" }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {((currentPage - 1) * rowsPerPage) + 1}–
                  {Math.min(currentPage * rowsPerPage, visibleCandidates.length)} of {visibleCandidates.length}
                </Typography>
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                )}
              </Box>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mt: 3 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                45-day Loyal Customers
                <Tooltip title={data?.loyaltyWindow?.rule || "At least 3 completed private consultations across at least 2 different days"}>
                  <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                First-consult cohort tracked from {data?.loyaltyWindow?.startDate || "—"} to {data?.loyaltyWindow?.endDate || "—"}.
                {" "}{data?.loyaltyWindow?.isComplete ? "Window completed." : "Window is still collecting future consultation activity."}
              </Typography>
            </Box>
            <TableContainer>
              <Table sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc", "& .MuiTableCell-head": { fontWeight: 500 } }}>
                    <TableCell>Customer</TableCell>
                    <TableCell align="center">45-day consultations</TableCell>
                    <TableCell align="center">Repeat consultations</TableCell>
                    <TableCell align="center">Active days</TableCell>
                    <TableCell>Consultant behaviour</TableCell>
                    <TableCell>Support Tags</TableCell>
                    <TableCell>Last consultation</TableCell>
                    <TableCell>Loyalty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading && !data?.loyalCustomers?.length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        No user has qualified as loyal in this cohort yet.
                      </TableCell>
                    </TableRow>
                  )}
                  {(data?.loyalCustomers || []).map((item) => (
                    <TableRow key={item.userId} hover>
                      <TableCell>
                        <Typography fontWeight={550}>{item.name}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          ID:{" "}
                          <Link to={`${userRoutes.USER_DETAIL}/${item.userId}`} style={{ color: "#1976d2", textDecoration: "underline" }}>
                            {item.customerId || item.userId}
                          </Link>
                          {item.phone ? ` · ${item.phone}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{item.totalConsultations}</TableCell>
                      <TableCell align="center">{item.repeatConsultations}</TableCell>
                      <TableCell align="center">{item.activeDays}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.consultantPattern}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.uniqueConsultants} unique consultant(s)</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(item.tags || []).length
                            ? item.tags.map((tag) => <Chip key={tag} size="small" label={tag} variant="outlined" />)
                            : <Typography variant="caption" color="text.secondary">No tags</Typography>}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.lastConsultationAt
                          ? new Date(item.lastConsultationAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={item.loyaltyLevel === "Strong loyal" ? "success" : "primary"}
                          label={item.loyaltyLevel}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </div>
    </div>
  );
};

export default PowerCustomer;
